import SearchableMultiSelect from '../../components/SearchableMultiSelect';
import { Ionicons } from '@expo/vector-icons';
import InventoryBatchPanel from '../../components/InventoryBatchPanel';
import StoreBatchSettings from '../../components/StoreBatchSettings';
import DateStatusDots, { dateSignalsLabel } from '../../components/DateStatusDots';
import { buildInventoryDateSignals, inventoryDay } from '../../utils/inventoryDateSignals';
import { Dashboard, dashboard, operation } from '../../services/operationsService';
import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import {
  View, Text, FlatList, TouchableOpacity, StyleSheet,
  TextInput, Modal, ActivityIndicator, ScrollView, Linking
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../../hooks/useAuth';
import { getStores, createStore, deleteStore, getStoreInventory, getStoreMinQuantities, saveStoreMinQuantities } from '../../services/storeService';
import { getEquipment, getCategories } from '../../services/inventoryService';
import { Store, StoreInventory, StoreMinQuantity, Equipment, Category, StoreOrder, StoreOrderItem } from '../../types';
import CrossPlatformAlert from '../../components/CrossPlatformAlert';
import { useLanguage } from '../../contexts/LanguageContext';
import { NavBackButton } from '../../components/NavBackButton';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import StoreOrderWizard from '../../components/StoreOrderWizard';
import { createStoreOrder, getStoreOrders } from '../../services/storeOrderService';

function OrderHistoryModal({
  visible, orders, stores, loading, onClose, onRefresh,
}: {
  visible: boolean;
  orders: StoreOrder[];
  stores: Store[];
  loading: boolean;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const { t } = useLanguage();
  const [storeFilter, setStoreFilter] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const filtered = storeFilter ? orders.filter(order => order.storeId === storeFilter) : orders;

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View style={ostyles.container}>
        <View style={ostyles.header}>
          <Text style={ostyles.title}>{t('stores.orderHistory')}</Text>
          <View style={ostyles.headerActions}>
            <TouchableOpacity onPress={onRefresh}><Text style={ostyles.link}>{t('stores.orderRefresh')}</Text></TouchableOpacity>
            <TouchableOpacity onPress={onClose}><Text style={ostyles.close}>✕</Text></TouchableOpacity>
          </View>
        </View>
        <ScrollView horizontal style={ostyles.filters} contentContainerStyle={ostyles.filtersContent}>
          <TouchableOpacity style={[ostyles.filter, !storeFilter && ostyles.filterActive]} onPress={() => setStoreFilter(null)}>
            <Text style={[ostyles.filterText, !storeFilter && ostyles.filterTextActive]}>{t('stores.orderAllStores')}</Text>
          </TouchableOpacity>
          {stores.map(store => (
            <TouchableOpacity key={store.id} style={[ostyles.filter, storeFilter === store.id && ostyles.filterActive]} onPress={() => setStoreFilter(store.id)}>
              <Text style={[ostyles.filterText, storeFilter === store.id && ostyles.filterTextActive]}>{store.name}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        {loading ? <ActivityIndicator style={{ marginTop: 40 }} color="#2563EB" size="large" /> : (
          <ScrollView contentContainerStyle={ostyles.list}>
            {filtered.map(order => {
              const expanded = expandedId === order.id;
              return (
                <View key={order.id} style={ostyles.card}>
                  <TouchableOpacity style={ostyles.cardHeader} onPress={() => setExpandedId(expanded ? null : order.id)}>
                    <View style={{ flex: 1 }}>
                      <Text style={ostyles.orderNumber}>{order.orderNumber}</Text>
                      <Text style={ostyles.meta}>{order.storeName} · {order.createdAt.toLocaleString()}</Text>
                      <Text style={ostyles.meta}>{t('stores.orderAuthor')}: {order.requestedByName}</Text>
                    </View>
                    <Text style={[ostyles.status, order.status === 'error' && ostyles.statusError]}>
                      {t(`stores.orderStatus${order.status === 'done' ? 'Done' : order.status === 'error' ? 'Error' : 'Pending'}`)}
                    </Text>
                  </TouchableOpacity>
                  {expanded && (
                    <View style={ostyles.details}>
                      {order.items.map(item => (
                        <View key={item.equipmentId} style={ostyles.detailRow}>
                          <View style={{ flex: 1 }}>
                            <Text style={ostyles.itemName}>{item.equipmentName}</Text>
                            <Text style={ostyles.meta}>{item.categoryName} · {t('stores.orderCurrentStock').replace('%s', `${item.currentQuantity} ${item.unit}`)}</Text>
                          </View>
                          <Text style={ostyles.itemQty}>{item.orderedQuantity} {item.unit}</Text>
                        </View>
                      ))}
                      {order.notes ? <Text style={ostyles.notes}>{t('stores.orderNotes')}: {order.notes}</Text> : null}
                      {order.errorMessage ? <Text style={ostyles.error}>{order.errorMessage}</Text> : null}
                    </View>
                  )}
                  {order.status === 'done' && order.downloadUrl && (
                    <TouchableOpacity style={ostyles.download} onPress={() => Linking.openURL(order.downloadUrl!)}>
                      <Text style={ostyles.downloadText}>{t('stores.orderDownload')}</Text>
                    </TouchableOpacity>
                  )}
                </View>
              );
            })}
            {filtered.length === 0 && <Text style={ostyles.empty}>{t('stores.orderNoOrders')}</Text>}
          </ScrollView>
        )}
      </View>
    </Modal>
  );
}

const ostyles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  header: { padding: 18, backgroundColor: '#FFF', borderBottomWidth: 1, borderBottomColor: '#E2E8F0', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: 22, fontWeight: '700', color: '#1E293B' },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 18 },
  link: { color: '#2563EB', fontWeight: '600' },
  close: { fontSize: 22, color: '#64748B' },
  filters: { maxHeight: 62, backgroundColor: '#FFF' },
  filtersContent: { padding: 12, gap: 8 },
  filter: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 18, backgroundColor: '#F1F5F9' },
  filterActive: { backgroundColor: '#2563EB' },
  filterText: { color: '#475569' },
  filterTextActive: { color: '#FFF' },
  list: { padding: 14, maxWidth: 850, width: '100%', alignSelf: 'center' },
  card: { backgroundColor: '#FFF', borderRadius: 10, marginBottom: 12, padding: 14, borderWidth: 1, borderColor: '#E2E8F0' },
  cardHeader: { flexDirection: 'row', alignItems: 'center' },
  orderNumber: { color: '#1E293B', fontWeight: '700', fontSize: 16 },
  meta: { color: '#64748B', fontSize: 12, marginTop: 3 },
  status: { color: '#D97706', fontWeight: '600', marginLeft: 8 },
  statusError: { color: '#DC2626' },
  details: { marginTop: 12, borderTopWidth: 1, borderTopColor: '#E2E8F0' },
  detailRow: { flexDirection: 'row', paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  itemName: { color: '#334155', fontWeight: '600' },
  itemQty: { color: '#2563EB', fontWeight: '700' },
  notes: { marginTop: 12, color: '#475569' },
  error: { marginTop: 10, color: '#DC2626' },
  download: { alignSelf: 'flex-end', marginTop: 12, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 7, backgroundColor: '#16A34A' },
  downloadText: { color: '#FFF', fontWeight: '700' },
  empty: { textAlign: 'center', marginTop: 40, color: '#64748B' },
});

interface CategoryNode {
  category: Category;
  children: CategoryNode[];
  inventoryItems: { equipment: Equipment; quantity: number; limit: number }[];
}

function buildCategoryTree(
  allCategories: Category[],
  storeInventory: StoreInventory[],
  equipment: Equipment[],
  limitMap: Record<string, number>,
  parentId: string | null = null,
): CategoryNode[] {
  const levelCategories = allCategories.filter(c => (c.parentId ?? null) === parentId);
  const nodes: CategoryNode[] = [];

  for (const cat of levelCategories) {
    const directItems: { equipment: Equipment; quantity: number; limit: number }[] = [];
    for (const inv of storeInventory) {
      const eq = equipment.find(e => e.id === inv.equipmentId);
      if (eq?.categoryId === cat.id) {
        directItems.push({ equipment: eq, quantity: inv.quantity, limit: limitMap[eq.id] ?? eq.minQuantity });
      }
    }

    const children = buildCategoryTree(allCategories, storeInventory, equipment, limitMap, cat.id);

    if (directItems.length > 0 || children.length > 0) {
      directItems.sort((a, b) => a.equipment.name.localeCompare(b.equipment.name));
      nodes.push({ category: cat, children, inventoryItems: directItems });
    }
  }

  return nodes;
}

function collectAllCategoryIds(nodes: CategoryNode[]): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    ids.push(node.category.id);
    ids.push(...collectAllCategoryIds(node.children));
  }
  return ids;
}

// ---------------------------------------------------------------------------
// Limits modal: shows all equipment from catalogue grouped by category
// ---------------------------------------------------------------------------

interface LimitsModalProps {
  visible: boolean;
  store: Store;
  categories: Category[];
  equipment: Equipment[];
  storeMinQuantities: StoreMinQuantity[];
  onClose: () => void;
  onSave: (limits: Record<string, number>) => Promise<void>;
}

function LimitsModal({ visible, store, categories, equipment, storeMinQuantities, onClose, onSave }: LimitsModalProps) {
  const { t } = useLanguage();
  const [limits, setLimits] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      // Initialise with store-specific overrides; fall back to catalogue default
      const initial: Record<string, number> = {};
      const overrideMap: Record<string, number> = {};
      for (const smq of storeMinQuantities) {
        overrideMap[smq.equipmentId] = smq.minQuantity;
      }
      for (const eq of equipment) {
        initial[eq.id] = overrideMap[eq.id] ?? eq.minQuantity;
      }
      setLimits(initial);
    }
  }, [visible, equipment, storeMinQuantities]);

  const adjust = (equipmentId: string, delta: number) => {
    setLimits(prev => ({ ...prev, [equipmentId]: Math.max(0, (prev[equipmentId] ?? 0) + delta) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(limits);
      onClose();
    } catch (err: any) {
      CrossPlatformAlert.alert(t('common.error'), err.message);
    } finally {
      setSaving(false);
    }
  };

  // Group equipment by category (only leaf-level: categories that have equipment directly assigned)
  const catMap: Record<string, Category> = {};
  for (const c of categories) { catMap[c.id] = c; }

  // Sort categories by weight, then name; sort equipment alphabetically within each category
  const catEquipment: Record<string, Equipment[]> = {};
  for (const eq of equipment) {
    if (!catEquipment[eq.categoryId]) catEquipment[eq.categoryId] = [];
    catEquipment[eq.categoryId].push(eq);
  }
  const usedCatIds = Object.keys(catEquipment);
  const sortedCats = usedCatIds
    .map(id => catMap[id])
    .filter(Boolean)
    .sort((a, b) => {
      if (a.weight !== b.weight) return a.weight - b.weight;
      return a.name.localeCompare(b.name);
    });

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={lstyles.overlay}>
        <View style={lstyles.sheet}>
          <Text style={lstyles.title}>{t('stores.limitsTitle').replace('%s', store.name)}</Text>
          <ScrollView style={lstyles.scroll} contentContainerStyle={{ paddingBottom: 8 }}>
            {sortedCats.map(cat => (
              <View key={cat.id}>
                <Text style={lstyles.catHeader}>{cat.name}</Text>
                {(catEquipment[cat.id] ?? [])
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(eq => (
                    <View key={eq.id} style={lstyles.row}>
                      <View style={lstyles.eqNameCol}>
                        <Text style={lstyles.eqName} numberOfLines={1}>{eq.name}</Text>
                        {eq.minQuantity > 0 && (
                          <Text style={lstyles.eqDefaultHint}>
                            {t('stores.limitsDefaultHint').replace('%s', String(eq.minQuantity))}
                          </Text>
                        )}
                      </View>
                      <View style={lstyles.qtyRow}>
                        <TouchableOpacity style={lstyles.qtyBtn} onPress={() => adjust(eq.id, -1)}>
                          <Text style={lstyles.qtyBtnText}>−</Text>
                        </TouchableOpacity>
                        <TextInput
                          style={lstyles.qtyInput}
                          value={String(limits[eq.id] ?? 0)}
                          onChangeText={v => {
                            const n = parseInt(v, 10);
                            setLimits(prev => ({ ...prev, [eq.id]: isNaN(n) || n < 0 ? 0 : n }));
                          }}
                          keyboardType="number-pad"
                        />
                        <TouchableOpacity style={lstyles.qtyBtn} onPress={() => adjust(eq.id, 1)}>
                          <Text style={lstyles.qtyBtnText}>+</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  ))}
              </View>
            ))}
          </ScrollView>
          <View style={lstyles.actions}>
            <TouchableOpacity style={lstyles.cancelBtn} onPress={onClose}>
              <Text style={lstyles.cancelText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={lstyles.saveBtn} onPress={handleSave} disabled={saving}>
              {saving ? <ActivityIndicator color="#FFF" /> : <Text style={lstyles.saveText}>{t('common.save')}</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const lstyles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  sheet: { backgroundColor: '#FFF', borderRadius: 20, padding: 24, maxHeight: '90%', width: '100%', maxWidth: 560 },
  title: { fontSize: 18, fontWeight: 'bold', color: '#1E293B', marginBottom: 12 },
  scroll: { flexGrow: 0 },
  catHeader: { fontSize: 13, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#E2E8F0', marginTop: 4 },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  eqNameCol: { flex: 1, marginRight: 8 },
  eqName: { fontSize: 14, color: '#1E293B' },
  eqDefaultHint: { fontSize: 11, color: '#94A3B8', marginTop: 1 },
  qtyRow: { flexDirection: 'row', alignItems: 'center' },
  qtyBtn: { backgroundColor: '#E2E8F0', width: 32, height: 32, borderRadius: 6, justifyContent: 'center', alignItems: 'center' },
  qtyBtnText: { fontSize: 18, color: '#1E293B', lineHeight: 22 },
  qtyInput: { width: 48, borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 6, padding: 4, fontSize: 14, textAlign: 'center', marginHorizontal: 4 },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 12 },
  cancelBtn: { paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0' },
  cancelText: { color: '#64748B', fontWeight: '600' },
  saveBtn: { backgroundColor: '#2563EB', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8, minWidth: 80, alignItems: 'center' },
  saveText: { color: '#FFF', fontWeight: '600' },
});

// ---------------------------------------------------------------------------
// Main screen
// ---------------------------------------------------------------------------

export default function StoresScreen() {
  const { appUser } = useAuth();
  const { t } = useLanguage();
  const router = useRouter();
  const params = useLocalSearchParams<{ storeId?: string }>();
  const navigation = useNavigation();
  const [stores, setStores] = useState<Store[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [storeInventory, setStoreInventory] = useState<StoreInventory[]>([]);
  const [storeMinQuantities, setStoreMinQuantities] = useState<StoreMinQuantity[]>([]);
  const [loading, setLoading] = useState(true);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(new Set());
  const [expandedDateIds, setExpandedDateIds] = useState<Set<string>>(new Set());
  const [today, setToday] = useState(inventoryDay);
  useEffect(() => {
    const timer = setInterval(() => setToday(inventoryDay()), 60000);
    return () => clearInterval(timer);
  }, []);
  const [modalVisible, setModalVisible] = useState(false);
  const [limitsModalStore, setLimitsModalStore] = useState<Store | null>(null);
  const [limitsForStore, setLimitsForStore] = useState<StoreMinQuantity[]>([]);
  const [editing, setEditing] = useState<Store | null>(null);
  const [storeName, setStoreName] = useState('');
  const [storeWeight, setStoreWeight] = useState('0');
  const [caretakerIds, setCaretakerIds] = useState<string[]>([]);
  const [caretakerOptions, setCaretakerOptions] = useState<{id: string; name: string}[]>([]);
  const [caretakerLoading, setCaretakerLoading] = useState(false);
  const [caretakerError, setCaretakerError] = useState(false);
  const [storeSaving, setStoreSaving] = useState(false);
  const [orderStore, setOrderStore] = useState<Store | null>(null);
  const [orderInventory, setOrderInventory] = useState<StoreInventory[]>([]);
  const [orders, setOrders] = useState<StoreOrder[]>([]);
  const [ordersVisible, setOrdersVisible] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [batchData, setBatchData] = useState<Dashboard | null>(null);
  const [batchError, setBatchError] = useState('');
  const selectedStoreRef = useRef('');
  const editingStoreRef = useRef('');

  const canManage = appUser?.role === 'admin' || appUser?.role === 'manager';
  const canView = appUser?.role !== 'guest';

  useLayoutEffect(() => {
    if (selectedStore) {
      navigation.setOptions({
        title: selectedStore.name,
        headerLeft: () => <NavBackButton onPress={() => setSelectedStore(null)} />,
      });
    } else {
      navigation.setOptions({
        title: t('tabs.stores'),
        headerLeft: () => <NavBackButton />,
      });
    }
  }, [selectedStore, navigation, t]);

  const load = async () => {
    setLoading(true);
    const [s, eq, cats] = await Promise.all([getStores(), getEquipment(), getCategories()]);
    setStores(s);
    setEquipment(eq);
    setAllCategories(cats);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const store = stores.find(s => s.id === params.storeId);
    if (store && selectedStoreRef.current !== store.id) void selectStore(store);
  }, [stores, params.storeId]);

  useFocusEffect(React.useCallback(() => {
    const id = selectedStore?.id;
    if (!id) return;
    let active = true;
    setInventoryLoading(true);
    setBatchData(null); setBatchError('');
    Promise.all([dashboard(id, 'store'), getStoreInventory(id), getStoreMinQuantities(id)]).then(([data, inventory, limits]) => {
      if (active && selectedStoreRef.current === id) {
        setBatchData(data); setStoreInventory(inventory); setStoreMinQuantities(limits);
      }
    }).catch(e => { if (active) setBatchError(String(e)); })
      .finally(() => { if (active) setInventoryLoading(false); });
    return () => { active = false; };
  }, [selectedStore?.id]));

  const selectStore = (store: Store) => {
    selectedStoreRef.current = store.id;
    setSelectedStore(store);
    setInventoryLoading(true);
    setBatchData(null); setBatchError('');
    setStoreInventory([]); setStoreMinQuantities([]);
    setExpandedCategoryIds(new Set());
    setExpandedDateIds(new Set());
  };

  const openCreate = () => {
    editingStoreRef.current = '';
    setCaretakerLoading(false);
    setEditing(null);
    setCaretakerIds([]); setCaretakerError(false);
    setStoreName('');
    setStoreWeight('0');
    setModalVisible(true);
  };

  const openEdit = (store: Store) => {
    editingStoreRef.current = store.id;
    setEditing(store);
    setCaretakerIds(store.caretakerIds ?? []); setCaretakerError(false); setCaretakerLoading(true);
    dashboard(store.id, 'directory').then(data => { if (editingStoreRef.current !== store.id) return; setCaretakerOptions(data.profiles); setCaretakerIds(data.stores.find(s => s.id === store.id)?.caretakerIds ?? store.caretakerIds ?? []); })
      .catch(e => { if (editingStoreRef.current !== store.id) return; setCaretakerError(true); CrossPlatformAlert.alert(t('common.error'), String(e)); })
      .finally(() => { if (editingStoreRef.current === store.id) setCaretakerLoading(false); });
    setStoreName(store.name);
    setStoreWeight(String(store.weight));
    setModalVisible(true);
  };

  const openLimits = async (store: Store) => {
    const smq = await getStoreMinQuantities(store.id);
    setLimitsForStore(smq);
    setLimitsModalStore(store);
  };

  const openOrder = async (store: Store) => {
    const inventoryForStore = selectedStore?.id === store.id
      ? storeInventory
      : await getStoreInventory(store.id);
    setOrderInventory(inventoryForStore);
    setOrderStore(store);
  };

  const saveOrder = async (items: StoreOrderItem[], notes: string) => {
    if (!orderStore || !appUser) return;
    await createStoreOrder({
      store: orderStore,
      requestedBy: appUser.uid,
      requestedByEmail: appUser.email,
      requestedByName: `${appUser.firstname} ${appUser.lastname}`.trim() || appUser.email,
      notes,
      items,
    });
    CrossPlatformAlert.alert(t('stores.orderCreated'), t('stores.orderCreatedMessage'));
  };

  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      setOrders(await getStoreOrders());
    } catch (err: any) {
      CrossPlatformAlert.alert(t('common.error'), err.message);
    } finally {
      setOrdersLoading(false);
    }
  };

  const openOrders = () => {
    setOrdersVisible(true);
    loadOrders();
  };

  const handleSave = async () => {
    if (!storeName.trim()) {
      CrossPlatformAlert.alert(t('common.validation'), t('stores.nameRequired'));
      return;
    }
    const weight = parseInt(storeWeight, 10);
    const parsedWeight = isNaN(weight) ? 0 : weight;
    setStoreSaving(true);
    try {
      if (editing) {
        await operation('inventoryOperation', {action: 'settings', storeId: editing.id, caretakerIds, name: storeName.trim(), weight: parsedWeight});
      } else {
        await createStore(storeName, parsedWeight);
      }
      setModalVisible(false);
      load();
    } catch (err: any) {
      CrossPlatformAlert.alert(t('common.error'), err.message);
    } finally { setStoreSaving(false); }
  };

  const handleDelete = (store: Store) => {
    CrossPlatformAlert.alert(t('common.delete'), t('stores.confirmDelete', store.name), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'), style: 'destructive', onPress: async () => {
          await deleteStore(store.id);
          if (selectedStore?.id === store.id) setSelectedStore(null);
          load();
        }
      }
    ]);
  };

  const handleSaveLimits = async (limits: Record<string, number>) => {
    if (!limitsModalStore) return;
    const catalogueDefaults: Record<string, number> = {};
    for (const eq of equipment) {
      catalogueDefaults[eq.id] = eq.minQuantity;
    }
    await saveStoreMinQuantities(limitsModalStore.id, limits, catalogueDefaults);
    // Refresh store min quantities if we're currently viewing that store
    if (selectedStore?.id === limitsModalStore.id) {
      const smq = await getStoreMinQuantities(limitsModalStore.id);
      setStoreMinQuantities(smq);
    }
  };

  const toggleCategory = (id: string) => {
    setExpandedCategoryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  // Build limit map: store-specific overrides take precedence over catalogue defaults
  const limitMap: Record<string, number> = {};
  const smqMap: Record<string, number> = {};
  for (const smq of storeMinQuantities) {
    smqMap[smq.equipmentId] = smq.minQuantity;
  }
  for (const eq of equipment) {
    limitMap[eq.id] = smqMap[eq.id] ?? eq.minQuantity;
  }

  const categoryTree = buildCategoryTree(allCategories, storeInventory, equipment, limitMap);
  const dateSignals = useMemo(() => buildInventoryDateSignals(allCategories, equipment,
    batchData ? storeInventory : [], batchData?.batches ?? [], today),
  [allCategories, equipment, storeInventory, batchData, today]);
  const allCategoryIds = collectAllCategoryIds(categoryTree);
  const allExpanded = allCategoryIds.length > 0 && allCategoryIds.every(id => expandedCategoryIds.has(id));

  const handleRevealAll = () => setExpandedCategoryIds(new Set(allCategoryIds));
  const handleCollapseAll = () => setExpandedCategoryIds(new Set());

  const renderCategoryNode = (node: CategoryNode, depth: number = 0): React.ReactElement => {
    const isExpanded = expandedCategoryIds.has(node.category.id);
    const paddingLeft = 16 + depth * 16;
    const signals = dateSignals.categories.get(node.category.id);
    const statusLabel = dateSignalsLabel(signals, t);

    return (
      <View key={node.category.id}>
        <TouchableOpacity
          style={[styles.accordionHeader, { paddingLeft }]}
          onPress={() => toggleCategory(node.category.id)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityState={{ expanded: isExpanded }}
          accessibilityLabel={[node.category.name, statusLabel].filter(Boolean).join(', ')}
        >
          <Text style={styles.accordionChevron}>{isExpanded ? '▼' : '▶'}</Text>
          <View style={styles.accordionTitleWrap}>
            <View style={styles.categoryTitleRow}>
              <Text style={styles.accordionTitle}>{node.category.name}</Text>
              <DateStatusDots signals={signals} />
            </View>
            {node.category.description ? (
              <Text style={styles.accordionDesc}>{node.category.description}</Text>
            ) : null}
          </View>
        </TouchableOpacity>

        {isExpanded && (
          <View>
            {node.inventoryItems.map(({ equipment: eq, quantity, limit }) => {
              const isBelowLimit = limit > 0 && quantity < limit;
              const hasDates = !!(eq.tracksExpiry || eq.tracksReview);
              const datesExpanded = expandedDateIds.has(eq.id);
              return (
                <View key={eq.id}>
                <View style={[styles.invRow, { paddingLeft: paddingLeft + 28 }]}>
                  <Text style={[styles.invName, isBelowLimit && styles.invNameAlert]}>{eq.name}</Text>
                  <Text style={[styles.invQty, isBelowLimit && styles.invQtyAlert]}>
                    {quantity}{limit > 0 ? `/${limit}` : ''} {eq.unit}
                  </Text>
                  {hasDates && <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={[`${t(datesExpanded ? 'operations.hideDates' : 'operations.showDates')}: ${eq.name}`,
                      dateSignalsLabel(dateSignals.products.get(eq.id), t)].filter(Boolean).join(', ')}
                    accessibilityState={{ expanded: datesExpanded, disabled: !batchData }}
                    disabled={!batchData}
                    style={[styles.dateHistoryButton, datesExpanded && styles.dateHistoryButtonActive, !batchData && {opacity: 0.4}]}
                    onPress={() => setExpandedDateIds(previous => {
                      const next = new Set(previous);
                      if (next.has(eq.id)) next.delete(eq.id); else next.add(eq.id);
                      return next;
                    })}
                  >
                    <Ionicons name="time-outline" size={19} color="#2563EB" />
                    <DateStatusDots signals={dateSignals.products.get(eq.id)} overlay />
                  </TouchableOpacity>}
                </View>
                {hasDates && batchData && <View style={datesExpanded ? [styles.dateDetails, { marginLeft: paddingLeft + 40 }] : undefined}>
                <InventoryBatchPanel batches={batchData.batches.filter(b => b.equipmentId === eq.id)} quantity={quantity} unit={eq.unit} storeId={selectedStore!.id} equipmentId={eq.id}
                  hideToggle expanded={datesExpanded}
                  alertDays={Math.max(...batchData.alertDays)}
                  tracking={{ tracksExpiry: !!eq.tracksExpiry, tracksReview: !!eq.tracksReview }}
                  unresolved={batchData.positions.find(p => p.equipmentId === eq.id)?.unresolvedQuantity ?? 0}
                />
                </View>}
                </View>
              );
            })}
            {node.children.map(child => renderCategoryNode(child, depth + 1))}
          </View>
        )}
      </View>
    );
  };

  if (!canView) {
    return <View style={styles.center}><Text style={styles.noAccess}>{t('common.noAccess')}</Text></View>;
  }

  // ── Store Inventory View ──────────────────────────────────────────────────

  if (selectedStore) {
    return (
      <View style={styles.container}>
        <View style={styles.innerContent}>
          {/* Breadcrumb */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.breadcrumbBar}
            contentContainerStyle={styles.breadcrumbContent}
          >
            <TouchableOpacity onPress={() => setSelectedStore(null)}>
              <Text style={styles.breadcrumbItem}>{t('stores.title')}</Text>
            </TouchableOpacity>
            <Text style={styles.breadcrumbSep}>{'›'}</Text>
            <Text style={[styles.breadcrumbItem, styles.breadcrumbActive]}>{selectedStore.name}</Text>
          </ScrollView>

          {/* Header */}
          <View style={[styles.header, { flexWrap: 'wrap', gap: 8 }]}>
            <Text style={[styles.title, { flex: 0, width: '100%' }]} numberOfLines={2}>{selectedStore.name}</Text>
            {canManage && (
              <TouchableOpacity
                style={[styles.revealBtn, { marginRight: 8 }]}
                onPress={() => openLimits(selectedStore)}
              >
                <Text style={styles.revealBtnText}>{t('stores.setLimits')}</Text>
              </TouchableOpacity>
            )}
            {canManage && (
              <TouchableOpacity
                style={[styles.revealBtn, { marginRight: 8 }]}
                onPress={() => openOrder(selectedStore)}>
                <Text style={styles.revealBtnText}>{t('stores.orderButton')}</Text>
              </TouchableOpacity>
            )}
            {canManage && (
              <TouchableOpacity
                style={[styles.revealBtn, { marginRight: 8 }]}
                onPress={() => router.push({ pathname: '/(app)/usage', params: { storeId: selectedStore.id } })}>
                <Text style={styles.revealBtnText}>{t('stores.viewUsageHistory')}</Text>
              </TouchableOpacity>
            )}
            {allCategoryIds.length > 0 && (
              <TouchableOpacity
                style={styles.revealBtn}
                onPress={allExpanded ? handleCollapseAll : handleRevealAll}
              >
                <Text style={styles.revealBtnText}>
                  {allExpanded ? t('stores.collapseAll') : t('stores.revealAll')}
                </Text>
              </TouchableOpacity>
            )}
          </View>

          {!!batchError && <Text style={styles.empty}>{batchError}</Text>}
          {canManage && batchData && <StoreBatchSettings data={batchData} storeId={selectedStore.id} admin={appUser?.role === 'admin'} onRefresh={async () => {
            const data = await dashboard(selectedStore.id, 'store');
            setBatchData(data);
            const updated = { ...selectedStore, inventoryVersion: data.stores.find(s => s.id === selectedStore.id)?.inventoryVersion as 1 | 2 };
            setSelectedStore(updated);
            setStores(prev => prev.map(s => s.id === updated.id ? updated : s));
            setStoreInventory(await getStoreInventory(updated.id));
          }} />}
          {inventoryLoading ? (
            <ActivityIndicator style={{ marginTop: 40 }} size="large" color="#2563EB" />
          ) : storeInventory.length === 0 ? (
            <Text style={styles.empty}>{t('stores.noStock')}</Text>
          ) : (
            <ScrollView style={styles.flex1}>
              {categoryTree.map(node => renderCategoryNode(node))}
            </ScrollView>
          )}
        </View>

        {limitsModalStore && (
          <LimitsModal
            visible={!!limitsModalStore}
            store={limitsModalStore}
            categories={allCategories}
            equipment={equipment}
            storeMinQuantities={limitsForStore}
            onClose={() => setLimitsModalStore(null)}
            onSave={handleSaveLimits}
          />
        )}
        {orderStore && (
          <StoreOrderWizard
            visible={!!orderStore}
            store={orderStore}
            categories={allCategories}
            equipment={equipment}
            inventory={orderInventory}
            onClose={() => setOrderStore(null)}
            onSave={saveOrder}
          />
        )}
        <OrderHistoryModal
          visible={ordersVisible}
          orders={orders}
          stores={stores}
          loading={ordersLoading}
          onClose={() => setOrdersVisible(false)}
          onRefresh={loadOrders}
        />
      </View>
    );
  }

  // ── Stores List View ──────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <View style={styles.innerContent}>
        <View style={styles.header}>
          <Text style={styles.title}>{t('stores.title')}</Text>
          {canManage && (
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity style={styles.historyBtn} onPress={openOrders}>
                <Text style={styles.historyBtnText}>{t('stores.orderHistory')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.addBtn} onPress={openCreate}>
                <Text style={styles.addBtnText}>{t('common.add')}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {loading ? (
          <ActivityIndicator style={{ marginTop: 40 }} size="large" color="#2563EB" />
        ) : (
          <FlatList
            data={stores}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={styles.storeCard}
                onPress={() => selectStore(item)}
                activeOpacity={0.7}
              >
                <Text style={styles.storeName}>{item.name}</Text>
                <View style={styles.storeActions}>
                  {/* Column 1: open */}
                  <TouchableOpacity
                    onPress={(e) => { e.stopPropagation(); selectStore(item); }}
                    style={[styles.openBtn, styles.storeActionCol1]}
                  >
                    <Text style={styles.openBtnText}>{t('common.open')}</Text>
                  </TouchableOpacity>
                  {/* Column 2: history + limits */}
                  {canManage && (
                    <View style={styles.storeActionCol}>
                      <TouchableOpacity
                        onPress={(e) => { e.stopPropagation(); openOrder(item); }}
                        style={[styles.orderBtn, styles.storeActionBtn]}
                      >
                        <Text style={styles.orderBtnText}>{t('stores.orderButton')}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={(e) => { e.stopPropagation(); router.push({ pathname: '/(app)/usage', params: { storeId: item.id } }); }}
                        style={[styles.historyBtn, styles.storeActionBtn]}
                      >
                        <Text style={styles.historyBtnText}>{t('stores.viewUsageHistory')}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={(e) => { e.stopPropagation(); openLimits(item); }}
                        style={[styles.limitsBtn, styles.storeActionBtn]}
                      >
                        <Text style={styles.limitsBtnText}>{t('stores.setLimits')}</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                  {/* Column 3: edit + delete */}
                  {canManage && (
                    <View style={styles.storeActionCol}>
                      <TouchableOpacity
                        onPress={(e) => { e.stopPropagation(); openEdit(item); }}
                        style={[styles.editBtn, styles.storeActionBtn]}
                      >
                        <Text style={styles.editText}>{t('common.edit')}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={(e) => { e.stopPropagation(); handleDelete(item); }}
                        style={[styles.deleteBtn, styles.storeActionBtn]}
                      >
                        <Text style={styles.deleteText}>{t('common.del')}</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              </TouchableOpacity>
            )}
            ListEmptyComponent={<Text style={styles.empty}>{t('stores.noItems')}</Text>}
          />
        )}
      </View>

      <Modal visible={modalVisible} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{editing ? t('stores.editTitle') : t('stores.addTitle')}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('stores.namePlaceholder')}
              value={storeName}
              onChangeText={setStoreName}
            />
            <TextInput
              style={styles.input}
              placeholder={t('stores.weightPlaceholder')}
              value={storeWeight}
              onChangeText={setStoreWeight}
              keyboardType="number-pad"
            />
            {editing && <SearchableMultiSelect label={t('operations.caretakers')} options={caretakerOptions} value={caretakerIds} onChange={setCaretakerIds} disabled={caretakerLoading || storeSaving} />}
            {caretakerLoading && <ActivityIndicator />}
            <View style={styles.modalActions}>
              <TouchableOpacity disabled={storeSaving} style={styles.cancelBtn} onPress={() => setModalVisible(false)}>
                <Text style={styles.cancelText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity disabled={storeSaving || caretakerLoading || caretakerError} style={styles.saveBtn} onPress={handleSave}>
                <Text style={styles.saveText}>{t('common.save')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {limitsModalStore && (
        <LimitsModal
          visible={!!limitsModalStore}
          store={limitsModalStore}
          categories={allCategories}
          equipment={equipment}
          storeMinQuantities={limitsForStore}
          onClose={() => setLimitsModalStore(null)}
          onSave={handleSaveLimits}
        />
      )}
      {orderStore && (
        <StoreOrderWizard
          visible={!!orderStore}
          store={orderStore}
          categories={allCategories}
          equipment={equipment}
          inventory={orderInventory}
          onClose={() => setOrderStore(null)}
          onSave={saveOrder}
        />
      )}
      <OrderHistoryModal
        visible={ordersVisible}
        orders={orders}
        stores={stores}
        loading={ordersLoading}
        onClose={() => setOrdersVisible(false)}
        onRefresh={loadOrders}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  dateDetails: { marginRight: 12, marginBottom: 8, padding: 6, borderLeftWidth: 3, borderLeftColor: '#93C5FD', borderRadius: 6, backgroundColor: '#EFF6FF' },
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  innerContent: { flex: 1, maxWidth: 960, width: '100%', alignSelf: 'center' },
  flex1: { flex: 1 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  noAccess: { fontSize: 18, color: '#6B7280' },
  breadcrumbBar: { backgroundColor: '#EFF6FF', maxHeight: 40 },
  breadcrumbContent: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8 },
  breadcrumbItem: { fontSize: 13, color: '#2563EB' },
  breadcrumbActive: { color: '#1E293B', fontWeight: '600' },
  breadcrumbSep: { fontSize: 13, color: '#94A3B8', marginHorizontal: 4 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  title: { flex: 1, fontSize: 22, fontWeight: 'bold', color: '#1E293B' },
  addBtn: { backgroundColor: '#2563EB', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 },
  addBtnText: { color: '#FFF', fontWeight: '600' },
  revealBtn: { backgroundColor: '#EFF6FF', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8 },
  revealBtnText: { color: '#2563EB', fontSize: 13, fontWeight: '600' },
  storeCard: { backgroundColor: '#FFF', margin: 8, borderRadius: 12, padding: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 1 },
  storeName: { fontSize: 16, fontWeight: '600', color: '#1E293B', flex: 1, marginRight: 8 },
  storeWeight: { fontSize: 12, color: '#94A3B8', marginRight: 8 },
  storeActions: { flexDirection: 'row', gap: 8, alignItems: 'center' },
  storeActionCol: { flexDirection: 'column', gap: 6 },
  storeActionCol1: { justifyContent: 'center', alignSelf: 'center' },
  storeActionBtn: { minWidth: 72 },
  orderBtn: { backgroundColor: '#7C3AED', paddingHorizontal: 10, paddingVertical: 7, borderRadius: 6, alignItems: 'center' },
  orderBtnText: { color: '#FFF', fontWeight: '600', fontSize: 12 },
  openBtn: { backgroundColor: '#EFF6FF', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, alignItems: 'center' },
  openBtnText: { color: '#2563EB', fontSize: 12, fontWeight: '600', textAlign: 'center' },
  historyBtn: { backgroundColor: '#F0F9FF', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, alignItems: 'center' },
  historyBtnText: { color: '#0369A1', fontSize: 12, fontWeight: '600', textAlign: 'center' },
  limitsBtn: { backgroundColor: '#F0FDF4', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, alignItems: 'center' },
  limitsBtnText: { color: '#16A34A', fontSize: 12, fontWeight: '600', textAlign: 'center' },
  editBtn: { backgroundColor: '#EFF6FF', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, alignItems: 'center' },
  editText: { color: '#2563EB', fontSize: 12, fontWeight: '600', textAlign: 'center' },
  deleteBtn: { backgroundColor: '#FEF2F2', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, alignItems: 'center' },
  deleteText: { color: '#EF4444', fontSize: 12, fontWeight: '600', textAlign: 'center' },
  empty: { textAlign: 'center', color: '#94A3B8', marginTop: 40, fontSize: 16, padding: 16 },
  accordionHeader: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingRight: 16, backgroundColor: '#F8FAFC', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  accordionChevron: { fontSize: 12, color: '#64748B', marginRight: 8, width: 16 },
  accordionTitleWrap: { flex: 1 },
  categoryTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  accordionTitle: { fontSize: 15, fontWeight: '600', color: '#1E293B', flexShrink: 1 },
  accordionDesc: { fontSize: 12, color: '#94A3B8', marginTop: 2 },
  invRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10, paddingRight: 16, borderBottomWidth: 1, borderBottomColor: '#F1F5F9', backgroundColor: '#FFF' },
  invName: { fontSize: 14, color: '#475569', flex: 1 },
  invNameAlert: { color: '#DC2626' },
  invQty: { fontSize: 14, fontWeight: '600', color: '#1E293B' },
  invQtyAlert: { color: '#DC2626' },
  dateHistoryButton: { width: 34, height: 34, marginLeft: 8, borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  dateHistoryButtonActive: { backgroundColor: '#EFF6FF', borderColor: '#BFDBFE' },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalContent: { backgroundColor: '#FFF', borderRadius: 20, padding: 24, width: '100%', maxWidth: 480 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#1E293B', marginBottom: 16 },
  input: { borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, padding: 12, fontSize: 15, marginBottom: 12 },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 8 },
  cancelBtn: { paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0' },
  cancelText: { color: '#64748B', fontWeight: '600' },
  saveBtn: { backgroundColor: '#2563EB', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 },
  saveText: { color: '#FFF', fontWeight: '600' },
});
